import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Pickaxe, Zap, Coins, Activity, Lock, Power, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionHeader } from "@/components/MickChat";

export function MiningDashboard() {
  const { user, setUser, setAuthOpen, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);
  const tickRef = useRef();

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/mining/status");
      setStatus(data);
      setPending(data.pending_rewards);
    } catch {}
  };

  useEffect(() => {
    if (user) loadStatus();
  }, [user]);

  // smooth client-side accrual animation while active
  useEffect(() => {
    clearInterval(tickRef.current);
    if (status?.mining_active) {
      const ratePerSec = 0.35 * ((status.hash_rate || 40) / 40);
      tickRef.current = setInterval(() => {
        setPending((p) => +(p + ratePerSec / 5).toFixed(4));
      }, 200);
    }
    return () => clearInterval(tickRef.current);
  }, [status?.mining_active, status?.hash_rate]);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mining/toggle");
      if (data.mining_active) toast.success("Mining rig ONLINE — hash blocks incoming ⚡");
      else toast.success(`Rig offline. Banked ${data.claimed?.toFixed(4)} AOX Bucks`);
      await loadStatus();
      await refresh();
    } catch {
      toast.error("Rig error. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mining/claim");
      if (data.claimed > 0) toast.success(`Claimed ${data.claimed.toFixed(4)} AOX Bucks!`);
      else toast("Nothing to claim yet, hoodie.");
      setUser((u) => (u ? { ...u, aox_balance: data.aox_balance } : u));
      await loadStatus();
    } catch {
      toast.error("Claim failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="mining" className="relative py-20 px-4 lg:px-8 cyber-grid">
      <div className="max-w-5xl mx-auto">
        <SectionHeader
          eyebrow="Mining Terminal"
          title="AOX Bucks Mining Rig"
          desc="Fire up your hoodie node and mine AOX Bucks in real time. Balance is tied to your account."
        />

        {!user ? (
          <div className="bg-aox-card border border-aox-cyan/25 clip-chamfer p-12 text-center">
            <Lock className="mx-auto text-aox-cyan mb-4" size={40} />
            <h3 className="font-display text-xl font-bold text-white mb-2">
              Hoodie Node Required
            </h3>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">
              Become a verified hoodie node to activate your mining rig and start
              earning AOX Bucks.
            </p>
            <button
              onClick={() => setAuthOpen(true)}
              className="bg-aox-cyan text-aox-bg font-bold px-7 py-3 clip-chamfer hover:glow-cyan transition-all"
              data-testid="mining-login-btn"
            >
              Become a Hoodie
            </button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* balance + claim */}
            <div className="lg:col-span-2 bg-aox-card border border-aox-cyan/25 clip-chamfer p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="font-mono-aox text-xs uppercase tracking-widest text-aox-cyan mb-1">
                    Wallet Balance
                  </div>
                  <div className="flex items-baseline gap-2">
                    <Coins className="text-aox-green" size={28} />
                    <span
                      className="font-display text-4xl font-black text-white text-glow-green"
                      data-testid="mining-balance"
                    >
                      {(user.aox_balance || 0).toFixed(2)}
                    </span>
                    <span className="font-mono-aox text-sm text-aox-green">AOX</span>
                  </div>
                </div>
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${
                    status?.mining_active
                      ? "border-aox-green/40 bg-aox-green/10"
                      : "border-slate-700 bg-aox-bg"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${
                      status?.mining_active ? "bg-aox-green pulse-dot" : "bg-slate-600"
                    }`}
                  />
                  <span className="font-mono-aox text-[10px] uppercase tracking-widest text-slate-300">
                    {status?.mining_active ? "Mining" : "Idle"}
                  </span>
                </div>
              </div>

              <div className="bg-aox-bg border border-aox-green/20 rounded-lg p-5 mb-5">
                <div className="font-mono-aox text-xs uppercase tracking-widest text-slate-500 mb-1">
                  Unclaimed Rewards
                </div>
                <div className="flex items-baseline gap-2">
                  <span
                    className="font-mono-aox text-2xl font-bold text-aox-cyan"
                    data-testid="mining-pending"
                  >
                    {pending.toFixed(4)}
                  </span>
                  <span className="font-mono-aox text-xs text-aox-cyan/60">AOX Bucks</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={toggle}
                  disabled={busy}
                  className={`flex items-center gap-2 font-bold px-6 py-3 clip-chamfer transition-all disabled:opacity-60 ${
                    status?.mining_active
                      ? "border border-red-500/50 text-red-400 hover:bg-red-500/10"
                      : "bg-aox-green text-aox-bg hover:glow-green"
                  }`}
                  data-testid="mining-toggle-btn"
                >
                  {busy ? <Loader2 className="animate-spin" size={18} /> : <Power size={18} />}
                  {status?.mining_active ? "Stop Rig" : "Start Mining"}
                </button>
                <button
                  onClick={claim}
                  disabled={busy || pending <= 0}
                  className="flex items-center gap-2 bg-aox-cyan text-aox-bg font-bold px-6 py-3 clip-chamfer hover:glow-cyan transition-all disabled:opacity-50"
                  data-testid="mining-claim-btn"
                >
                  <Pickaxe size={18} />
                  Claim Rewards
                </button>
              </div>
            </div>

            {/* rig stats */}
            <div className="bg-aox-card border border-aox-cyan/25 clip-chamfer p-6 space-y-5">
              <div className="font-mono-aox text-xs uppercase tracking-widest text-aox-cyan">
                Rig Telemetry
              </div>
              <RigStat
                icon={Zap}
                label="Hash Rate"
                value={`${(status?.hash_rate || user.hash_rate || 0).toFixed(1)} TH/s`}
                active={status?.mining_active}
              />
              <RigStat
                icon={Activity}
                label="Rig Status"
                value={status?.mining_active ? "ACTIVE" : "STANDBY"}
                active={status?.mining_active}
              />
              <RigStat
                icon={Coins}
                label="Yield Rate"
                value={`${(0.35 * ((status?.hash_rate || 40) / 40)).toFixed(2)} AOX/s`}
                active={status?.mining_active}
              />
              <div className="pt-2">
                <div className="h-2 bg-aox-bg rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-aox-cyan to-aox-green"
                    animate={{ width: status?.mining_active ? ["20%", "95%", "20%"] : "8%" }}
                    transition={{ duration: 2.5, repeat: status?.mining_active ? Infinity : 0 }}
                  />
                </div>
                <div className="font-mono-aox text-[10px] text-slate-500 mt-2 uppercase tracking-widest">
                  {status?.mining_active ? "Hashing blocks…" : "Rig idle"}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function RigStat({ icon: Icon, label, value, active }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-slate-400">
        <Icon size={16} className={active ? "text-aox-green" : "text-aox-cyan/60"} />
        <span className="text-sm">{label}</span>
      </div>
      <span className={`font-mono-aox text-sm font-bold ${active ? "text-aox-green" : "text-white"}`}>
        {value}
      </span>
    </div>
  );
}
